import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Divider,
  CircularProgress,
  LinearProgress,
  Link,
  Tooltip,
  Chip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UserAvatar from './UserAvatar';
import { filesAPI, messagesAPI } from '../api';
import { getApiBaseUrl } from '../utils/apiConfig';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Memoized ChatMessage component to prevent unnecessary rerenders
const ChatMessage = React.memo(({ 
  message, 
  isOwn, 
  currentUser,
  users,
  conversationType,
  expandedMessages,
  copiedMessageId,
  onToggleExpand,
  onCopyMessage,
  onEditMessage,
  onDeleteMessage,
  isMessageLong,
  formatFileSize,
  API_BASE_URL: apiBaseUrl
}) => {
  const isExpanded = expandedMessages.has(message.id);
  const isLong = isMessageLong(message.message);
  
  // Helper function to construct file viewing URL (similar to download URL construction)
  const getFileViewUrl = useCallback((fileAttachment) => {
    if (!fileAttachment || !fileAttachment.fileUrl) return null;
    
    // Extract folderId and fileName from fileUrl
    // fileUrl format: /api/files/{folderId}/{fileName}
    const fileUrl = fileAttachment.fileUrl;
    const urlMatch = fileUrl.match(/\/api\/files\/([^/]+)\/(.+)$/);
    
    if (urlMatch) {
      const [, folderId, fileName] = urlMatch;
      // Construct URL properly: apiBaseUrl + /files/{folderId}/{fileName}
      if (apiBaseUrl && typeof apiBaseUrl === 'string') {
        // apiBaseUrl is like https://api.fastchat.maikvdhengel.me/api
        // We want: https://api.fastchat.maikvdhengel.me/api/files/...
        return `${apiBaseUrl}/files/${folderId}/${encodeURIComponent(fileName)}`;
      } else {
        // Fallback to relative URL
        return `/api/files/${folderId}/${encodeURIComponent(fileName)}`;
      }
    }
    
    // Fallback to original URL if parsing fails
    return fileUrl;
  }, [apiBaseUrl]);
  
  // Handle file download
  const handleDownload = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!message.fileAttachment) return;
    
    // Extract folderId and fileName from fileUrl
    // fileUrl format: /api/files/{folderId}/{fileName}
    const fileUrl = message.fileAttachment.fileUrl;
    const urlMatch = fileUrl.match(/\/api\/files\/([^/]+)\/(.+)$/);
    
    if (urlMatch) {
      const [, folderId, fileName] = urlMatch;
      // Use download endpoint that forces download
      // Construct URL properly: replace /api/files/ with /api/files/download/
      let downloadUrl;
      if (apiBaseUrl && typeof apiBaseUrl === 'string') {
        // apiBaseUrl is like https://api.fastchat.maikvdhengel.me/api
        // We want: https://api.fastchat.maikvdhengel.me/api/files/download/...
        downloadUrl = `${apiBaseUrl}/files/download/${folderId}/${encodeURIComponent(fileName)}`;
      } else {
        // Fallback to relative URL
        downloadUrl = `/api/files/download/${folderId}/${encodeURIComponent(fileName)}`;
      }
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = message.fileAttachment.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Fallback to original URL if parsing fails
      const fallbackUrl = apiBaseUrl && typeof apiBaseUrl === 'string' 
        ? `${apiBaseUrl.replace('/api', '')}${fileUrl}` 
        : fileUrl;
      const link = document.createElement('a');
      link.href = fallbackUrl;
      link.download = message.fileAttachment.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [message.fileAttachment, apiBaseUrl]);
  
  // Extract preview text when collapsed
  const getPreviewContent = useCallback((content) => {
    if (!content) return '';
    
    // Extract plain text from HTML
    let textContent = content;
    if (content.includes('<')) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      textContent = tempDiv.textContent || tempDiv.innerText || '';
    }
    
    // Get first ~300 characters (approximately 6 lines)
    const preview = textContent.substring(0, 300);
    if (textContent.length > 300) {
      return preview + '...';
    }
    return preview;
  }, []);

  // Only render full content when expanded, otherwise render preview
  const renderMessageContent = useCallback(() => {
    // Show "Deleted" for deleted messages
    if (message.deleted) {
      return (
        <Box 
          sx={{ 
            mb: message.fileAttachment ? 1 : 0,
            color: isOwn ? 'white' : 'text.primary',
            fontStyle: 'italic',
            opacity: 0.7,
          }}
        >
          Deleted
        </Box>
      );
    }
    
    if (!message.message) return null;
    
    if (isLong && !isExpanded) {
      // Render preview when collapsed
      const preview = getPreviewContent(message.message);
      return (
        <Box 
          sx={{ 
            mb: message.fileAttachment ? 1 : 0,
            color: isOwn ? 'white' : 'text.primary',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.5,
          }}
        >
          {preview}
        </Box>
      );
    }
    
    // Render full content when expanded
    return (
      <Box 
        sx={{ 
          mb: message.fileAttachment ? 1 : 0,
          maxHeight: 'none',
          overflow: 'visible',
          position: 'relative',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          lineHeight: 1.5,
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: 1,
            marginTop: 1,
            marginBottom: 1,
            display: 'block',
          },
          '& p': {
            margin: 0,
            marginBottom: 0,
            padding: 0,
            whiteSpace: 'pre-wrap',
            lineHeight: 'inherit',
            '&:last-child': {
              marginBottom: 0,
            },
          },
          '& br': {
            lineHeight: 'inherit',
          },
          '& pre': {
            whiteSpace: 'pre-wrap',
            margin: 0,
            marginBottom: 0,
            padding: 0,
            lineHeight: 'inherit',
            '&:last-child': {
              marginBottom: 0,
            },
          },
          color: isOwn ? 'white' : 'text.primary',
          '& strong': {
            fontWeight: 'bold',
          },
          '& em': {
            fontStyle: 'italic',
          },
          '& u': {
            textDecoration: 'underline',
          },
          '& s': {
            textDecoration: 'line-through',
          },
        }}
        dangerouslySetInnerHTML={{ 
          __html: message.message.includes('<') ? message.message : `<p>${message.message.replace(/\n/g, '<br>')}</p>`
        }}
      />
    );
  }, [message.message, message.fileAttachment, isLong, isExpanded, isOwn, getPreviewContent]);

  return (
    <ListItem
      sx={{
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        px: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isOwn ? 'flex-end' : 'flex-start',
          maxWidth: '70%',
        }}
      >
        {(conversationType === 'group' || !isOwn) && (() => {
          const sender = users.find(u => u.id === message.senderId);
          const profilePictureUrl = sender?.profilePictureUrl || message.senderProfilePictureUrl || null;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <UserAvatar 
                name={message.senderName} 
                size={24} 
                profilePictureUrl={profilePictureUrl}
              />
              <Typography variant="caption" color="text.secondary">
                {message.senderName}
              </Typography>
            </Box>
          );
        })()}
        <Paper
          sx={{
            p: 1.5,
            bgcolor: isOwn ? 'primary.main' : 'white',
            color: isOwn ? 'white' : 'text.primary',
            borderRadius: 2,
            position: 'relative',
            '&:hover .message-actions': {
              opacity: 1,
            },
          }}
        >
          <Box
            className="message-actions"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              display: 'flex',
              gap: 0.5,
              opacity: 0,
              transition: 'opacity 0.2s',
              zIndex: 1,
            }}
          >
            {isOwn && !message.deleted && !message.fileAttachment && (
              <Tooltip title="Edit message">
                <IconButton
                  size="small"
                  onClick={() => onEditMessage(message)}
                  sx={{
                    bgcolor: isOwn ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.05)',
                    color: isOwn ? 'white' : 'text.primary',
                    '&:hover': {
                      bgcolor: isOwn ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)',
                    },
                    width: 24,
                    height: 24,
                  }}
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
            {isOwn && !message.deleted && (
              <Tooltip title="Delete message">
                <IconButton
                  size="small"
                  onClick={() => onDeleteMessage(message)}
                  sx={{
                    bgcolor: isOwn ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.05)',
                    color: isOwn ? 'white' : 'text.primary',
                    '&:hover': {
                      bgcolor: isOwn ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)',
                    },
                    width: 24,
                    height: 24,
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
            {!message.deleted && (
              <Tooltip title={copiedMessageId === message.id ? 'Copied!' : 'Copy message'}>
                <IconButton
                  size="small"
                  onClick={() => onCopyMessage(message.id, message.message)}
                  sx={{
                    bgcolor: isOwn ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.05)',
                    color: isOwn ? 'white' : 'text.primary',
                    '&:hover': {
                      bgcolor: isOwn ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)',
                    },
                    width: 24,
                    height: 24,
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {renderMessageContent()}
          {isLong && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mt: 0.5,
              }}
            >
              <Box
                component="button"
                onClick={() => onToggleExpand(message.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: isOwn ? 'white' : 'text.primary',
                  opacity: 0.7,
                  padding: '4px 8px',
                  borderRadius: 1,
                  '&:hover': {
                    opacity: 1,
                    bgcolor: isOwn ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                {isExpanded ? (
                  <>
                    <ExpandLessIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      Show less
                    </Typography>
                  </>
                ) : (
                  <>
                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      Show more
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )}
          {message.fileAttachment && (
            <Box sx={{ mb: message.message ? 1 : 0 }}>
              {message.fileAttachment.fileName.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) ? (
                <Box sx={{ mt: 1 }}>
                  <img
                    src={getFileViewUrl(message.fileAttachment) || message.fileAttachment.fileUrl}
                    alt={message.fileAttachment.fileName}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '400px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      const url = getFileViewUrl(message.fileAttachment) || message.fileAttachment.fileUrl;
                      window.open(url, '_blank');
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        flex: 1,
                        opacity: 0.8,
                        color: isOwn ? 'white' : 'text.secondary',
                      }}
                    >
                      {message.fileAttachment.fileName} ({formatFileSize(message.fileAttachment.fileSize)})
                    </Typography>
                    <Tooltip title="Open in new tab">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = getFileViewUrl(message.fileAttachment) || message.fileAttachment.fileUrl;
                          window.open(url, '_blank');
                        }}
                        sx={{
                          color: isOwn ? 'white' : 'primary.main',
                          opacity: 0.8,
                          '&:hover': { opacity: 1 },
                          padding: '4px',
                        }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        onClick={handleDownload}
                        sx={{
                          color: isOwn ? 'white' : 'primary.main',
                          opacity: 0.8,
                          '&:hover': { opacity: 1 },
                          padding: '4px',
                        }}
                      >
                        <DownloadIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Link
                    href={getFileViewUrl(message.fileAttachment) || message.fileAttachment.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      flex: 1,
                      color: isOwn ? 'white' : 'primary.main',
                      textDecoration: 'underline',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <AttachFileIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2">
                      {message.fileAttachment.fileName}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      ({formatFileSize(message.fileAttachment.fileSize)})
                    </Typography>
                  </Link>
                  <Tooltip title="Open in new tab">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const url = getFileViewUrl(message.fileAttachment) || message.fileAttachment.fileUrl;
                        window.open(url, '_blank');
                      }}
                      sx={{
                        color: isOwn ? 'white' : 'primary.main',
                        opacity: 0.8,
                        '&:hover': { opacity: 1 },
                        padding: '4px',
                      }}
                    >
                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      onClick={handleDownload}
                      sx={{
                        color: isOwn ? 'white' : 'primary.main',
                        opacity: 0.8,
                        '&:hover': { opacity: 1 },
                        padding: '4px',
                      }}
                    >
                      <DownloadIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              opacity: 0.7,
              fontSize: '0.7rem',
            }}
          >
            {new Date(message.timestamp).toLocaleTimeString()}
            {message.editedAt && (
              <span style={{ marginLeft: '4px', fontStyle: 'italic' }}>
                (edited)
              </span>
            )}
          </Typography>
        </Paper>
      </Box>
    </ListItem>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent rerenders when props haven't changed
  // Returns true if props are equal (skip rerender), false if different (rerender)
  const fileAttachmentEqual = 
    (!prevProps.message.fileAttachment && !nextProps.message.fileAttachment) ||
    (prevProps.message.fileAttachment && nextProps.message.fileAttachment &&
     JSON.stringify(prevProps.message.fileAttachment) === JSON.stringify(nextProps.message.fileAttachment));
  
  const messageEqual = 
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.message === nextProps.message.message &&
    prevProps.message.timestamp === nextProps.message.timestamp &&
    prevProps.message.editedAt === nextProps.message.editedAt &&
    prevProps.message.deleted === nextProps.message.deleted &&
    fileAttachmentEqual;
  
  const expandedEqual = 
    prevProps.expandedMessages.has(prevProps.message.id) === nextProps.expandedMessages.has(nextProps.message.id);
  
  const otherPropsEqual = 
    prevProps.copiedMessageId === nextProps.copiedMessageId &&
    prevProps.isOwn === nextProps.isOwn &&
    prevProps.conversationType === nextProps.conversationType;
  
  return messageEqual && expandedEqual && otherPropsEqual;
});

ChatMessage.displayName = 'ChatMessage';

function ChatPanel({ 
  currentUser, 
  selectedUser,
  selectedGroup,
  messages, 
  onSendMessage, 
  conversationId,
  conversationType = 'direct',
  socket,
  onHideGroup,
  users = [],
  injectedAttachment = null,
  onInjectedAttachmentConsumed,
}) {
  const [messageText, setMessageText] = useState('');
  const [messageHtml, setMessageHtml] = useState('');
  const messagesEndRef = useRef(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pendingFileAttachment, setPendingFileAttachment] = useState(null);
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const fileInputRef = useRef(null);
  const quillRef = useRef(null);
  const typingTimeoutsRef = useRef({});

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Initialize API base URL
  useEffect(() => {
    const initApiBaseUrl = async () => {
      const url = await getApiBaseUrl();
      setApiBaseUrl(url);
    };
    initApiBaseUrl();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Clear editing state when conversation changes
  useEffect(() => {
    setEditingMessageId(null);
    setMessageText('');
    setMessageHtml('');
    setPendingFileAttachment(null);
    setUploadProgress(0);
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      quill.setContents([]);
    }
  }, [conversationId]);

  // Apply injected attachment(s) from the Files tab. Single attachment becomes
  // a pending attachment the user can add a message to; multiple attachments
  // are sent right away as separate messages so the user doesn't have to
  // press send N times.
  const lastInjectedKeyRef = useRef(null);
  useEffect(() => {
    if (!injectedAttachment || !conversationId) return;
    if (lastInjectedKeyRef.current === injectedAttachment.key) return;
    lastInjectedKeyRef.current = injectedAttachment.key;

    const attachments = injectedAttachment.attachments || [];
    if (attachments.length === 0) return;

    if (attachments.length === 1) {
      setPendingFileAttachment(attachments[0]);
    } else {
      attachments.forEach((att) => {
        const messageData = {
          conversationId,
          message: `Sent ${att.fileName}`,
          type: conversationType,
          fileAttachment: att,
        };
        if (conversationType === 'direct' && selectedUser) {
          messageData.recipientId = selectedUser.id;
        }
        onSendMessage(messageData);
      });
    }
    onInjectedAttachmentConsumed?.();
  }, [injectedAttachment, conversationId, conversationType, selectedUser, onSendMessage, onInjectedAttachmentConsumed]);

  useEffect(() => {
    if (!socket) return;

    const handleTypingStart = (data) => {
      if (data.conversationId === conversationId && data.userId !== currentUser.id) {
        // Clear any existing timeout for this user
        if (typingTimeoutsRef.current[data.userId]) {
          clearTimeout(typingTimeoutsRef.current[data.userId]);
          delete typingTimeoutsRef.current[data.userId];
        }

        setTypingUsers(prev => {
          if (!prev.includes(data.userId)) {
            return [...prev, data.userId];
          }
          return prev;
        });

        // Set a 30-second timeout to automatically remove the typing indicator
        typingTimeoutsRef.current[data.userId] = setTimeout(() => {
          setTypingUsers(prev => prev.filter(id => id !== data.userId));
          delete typingTimeoutsRef.current[data.userId];
        }, 30000); // 30 seconds
      }
    };

    const handleTypingStop = (data) => {
      if (data.conversationId === conversationId) {
        // Clear the timeout if user stops typing before 30 seconds
        if (typingTimeoutsRef.current[data.userId]) {
          clearTimeout(typingTimeoutsRef.current[data.userId]);
          delete typingTimeoutsRef.current[data.userId];
        }

        setTypingUsers(prev => prev.filter(id => id !== data.userId));
      }
    };

    socket.on('typing:start', handleTypingStart);
    socket.on('typing:stop', handleTypingStop);

    return () => {
      socket.off('typing:start', handleTypingStart);
      socket.off('typing:stop', handleTypingStop);
      
      // Clear all timeouts on cleanup
      Object.values(typingTimeoutsRef.current).forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      typingTimeoutsRef.current = {};
    };
  }, [socket, conversationId, currentUser.id]);

  const handleSend = (e, overrideText = null, overrideHtml = null) => {
    e.preventDefault();
    const textContent = (overrideText !== null ? overrideText : messageText).trim();
    const htmlContent = (overrideHtml !== null ? overrideHtml : messageHtml).trim();
    if ((!textContent && !htmlContent && !pendingFileAttachment && !uploading) || (!selectedUser && !selectedGroup)) return;

    const messageData = {
      conversationId,
      message: htmlContent || textContent || (pendingFileAttachment ? `Sent ${pendingFileAttachment.fileName}` : ''), // Send HTML if available, otherwise plain text
      type: conversationType,
    };

    // Include file attachment if present
    if (pendingFileAttachment) {
      messageData.fileAttachment = pendingFileAttachment;
    }

    if (conversationType === 'direct' && selectedUser) {
      messageData.recipientId = selectedUser.id;
    }

    // If editing a message, include the message ID
    if (editingMessageId) {
      messageData.messageId = editingMessageId;
      messageData.isEdit = true;
    }

    onSendMessage(messageData);

    setMessageText('');
    setMessageHtml('');
    setPendingFileAttachment(null);
    setEditingMessageId(null);
    
    // Clear Quill editor
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      quill.setContents([]);
    }
    
    // Stop typing indicator
    if (socket) {
      socket.emit('typing:stop', { conversationId });
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || (!selectedUser && !selectedGroup)) return;

    await processFile(file);
  };

  const processFile = async (file) => {
    if (!file || (!selectedUser && !selectedGroup)) return;

    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await filesAPI.uploadFile(
        file, 
        conversationId, 
        conversationType,
        (progress) => {
          setUploadProgress(progress);
        }
      );
      
      // Check if it's an image
      if (file.type.startsWith('image/')) {
        // For images, insert into Quill editor (like paste handler)
        const quill = quillRef.current?.getEditor();
        if (quill) {
          // Get current apiBaseUrl from state - if not ready, fetch it
          let currentApiBaseUrl = apiBaseUrl;
          if (!currentApiBaseUrl) {
            currentApiBaseUrl = await getApiBaseUrl();
            setApiBaseUrl(currentApiBaseUrl);
          }
          const fileUrl = `${currentApiBaseUrl.replace('/api', '')}${response.data.fileUrl}`;
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', fileUrl);
          quill.setSelection(range.index + 1);
          
          // Update state
          const text = quill.getText();
          const html = quill.root.innerHTML;
          setMessageText(text);
          setMessageHtml(html);
        }
      } else {
        // For other files, add as pending attachment (don't send yet)
        const fileAttachment = {
          fileId: response.data.fileId,
          fileName: response.data.fileName,
          fileSize: response.data.fileSize,
          fileUrl: response.data.fileUrl,
        };
        
        setPendingFileAttachment(fileAttachment);
      }
    } catch (error) {
      console.error('File upload error:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only show drag state if we have files
    if (e.dataTransfer.types.includes('Files')) {
      if (!isDragging) {
        setIsDragging(true);
      }
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Check if we're actually leaving the form element (not just moving to a child)
    const relatedTarget = e.relatedTarget;
    const currentTarget = e.currentTarget;
    
    // If relatedTarget is null or not a child of currentTarget, we're leaving
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!selectedUser && !selectedGroup) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Process the first file (you can extend this to handle multiple files if needed)
    const file = files[0];
    await processFile(file);
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemovePendingAttachment = () => {
    setPendingFileAttachment(null);
  };

  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }, []);

  const handleCopyMessage = useCallback(async (messageId, messageContent) => {
    try {
      // Extract plain text from HTML if needed
      let textToCopy = messageContent;
      if (messageContent.includes('<')) {
        // Create a temporary div to extract text from HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = messageContent;
        textToCopy = tempDiv.textContent || tempDiv.innerText || '';
      }
      
      await navigator.clipboard.writeText(textToCopy);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (error) {
      console.error('Failed to copy message:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = messageContent;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  }, []);

  const handleEditMessage = useCallback((message) => {
    setEditingMessageId(message.id);
    setMessageText(message.message);
    setMessageHtml(message.message);
    
    // Set Quill editor content
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      quill.root.innerHTML = message.message;
    }
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setMessageText('');
    setMessageHtml('');
    setPendingFileAttachment(null);
    
    // Clear Quill editor
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      quill.setContents([]);
    }
  }, []);

  const handleDeleteMessage = useCallback(async (message) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      if (socket) {
        // Use socket for real-time deletion
        socket.emit('message:delete', { messageId: message.id });
      } else {
        // Fallback to API call if socket is not available
        await messagesAPI.deleteMessage(message.id);
      }
    } catch (error) {
      console.error('Failed to delete message:', error);
      alert('Failed to delete message. Please try again.');
    }
  }, [socket]);

  const toggleMessageExpand = useCallback((messageId) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  }, []);

  const isMessageLong = useCallback((messageContent) => {
    if (!messageContent) return false;
    
    // Count lines in plain text
    let textContent = messageContent;
    if (messageContent.includes('<')) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = messageContent;
      textContent = tempDiv.textContent || tempDiv.innerText || '';
    }
    
    // Count newlines and estimate lines based on content length
    const lineCount = (textContent.match(/\n/g) || []).length + 1;
    // Also consider long lines that would wrap (assuming ~80 chars per line)
    const estimatedLines = Math.max(lineCount, Math.ceil(textContent.length / 80));
    
    return estimatedLines > 6;
  }, []);

  const handleTyping = (content, delta, source, editor) => {
    const text = editor.getText();
    const html = editor.getHTML();
    setMessageText(text);
    setMessageHtml(html);
    
    if (socket && text.trim()) {
      socket.emit('typing:start', { conversationId });
    } else if (socket) {
      socket.emit('typing:stop', { conversationId });
    }
  };

  // Memoize modules configuration
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link'],
      ['clean']
    ]
  }), []);

  // Use refs to store current values to avoid stale closures
  const uploadingRef = useRef(uploading);
  const selectedUserRef = useRef(selectedUser);
  const selectedGroupRef = useRef(selectedGroup);
  const handleSendRef = useRef(handleSend);

  // Update refs when values change
  useEffect(() => {
    uploadingRef.current = uploading;
    selectedUserRef.current = selectedUser;
    selectedGroupRef.current = selectedGroup;
    handleSendRef.current = handleSend;
  }, [uploading, selectedUser, selectedGroup, handleSend]);

  // Setup keyboard bindings for Enter to send, Shift+Enter for newline
  // This works for both direct chats and group chats
  useEffect(() => {
    if (!quillRef.current) return;

    let timeoutId = null;
    let intervalId = null;

    // Function to setup the keyboard bindings using Quill's keyboard module
    const setupKeyboardBindings = () => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return false;

      const keyboard = quill.keyboard;
      if (!keyboard) return false;

      // Clear all existing Enter key bindings first
      try {
        if (keyboard.bindings[13]) {
          // Create a new array with only Shift+Enter bindings
          keyboard.bindings[13] = keyboard.bindings[13].filter(binding => binding && binding.shiftKey);
        }
      } catch (e) {
        console.error('Error clearing Enter bindings:', e);
      }

      // Add custom Enter key binding (without Shift) to send message
      // This must be added before Shift+Enter to ensure proper priority
      try {
        keyboard.addBinding({
          key: 13, // Enter key
          handler: (range, context) => {
            // Get current content directly from Quill editor
            const textContent = quill.getText();
            const htmlContent = quill.root.innerHTML;
            const trimmedText = textContent.trim();
            const trimmedHtml = htmlContent.trim();
            
            // Use refs to get current values (avoid stale closures)
            const currentUploading = uploadingRef.current;
            const currentSelectedUser = selectedUserRef.current;
            const currentSelectedGroup = selectedGroupRef.current;
            const currentHandleSend = handleSendRef.current;
            
            // Only send if there's content or uploading
            // Works for both direct chats (selectedUser) and group chats (selectedGroup)
            if (trimmedText || trimmedHtml || currentUploading) {
              if (currentSelectedUser || currentSelectedGroup) {
                // Create a synthetic event for handleSend and pass content directly
                const syntheticEvent = { preventDefault: () => {} };
                currentHandleSend(syntheticEvent, textContent, htmlContent);
                return false; // Prevent default behavior (newline)
              }
            }
            return false; // Prevent default behavior even if no content
          }
        });
      } catch (e) {
        console.error('Error adding Enter binding:', e);
        return false;
      }

      // Ensure Shift+Enter still works for newline
      try {
        keyboard.addBinding({
          key: 13,
          shiftKey: true,
          handler: () => {
            return true; // Allow default behavior (newline)
          }
        });
      } catch (e) {
        console.error('Error adding Shift+Enter binding:', e);
      }

      return true; // Successfully set up
    };

    // Also add a direct event listener on the editor container as a fallback
    // This catches the event in the capture phase before Quill processes it
    const handleKeyDownCapture = (e) => {
      // Check if Enter key is pressed (keyCode 13 or key === 'Enter')
      if ((e.key === 'Enter' || e.keyCode === 13) && !e.shiftKey) {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        // Get current content directly from Quill editor
        const textContent = quill.getText();
        const htmlContent = quill.root.innerHTML;
        const trimmedText = textContent.trim();
        const trimmedHtml = htmlContent.trim();
        
        // Use refs to get current values (avoid stale closures)
        const currentUploading = uploadingRef.current;
        const currentSelectedUser = selectedUserRef.current;
        const currentSelectedGroup = selectedGroupRef.current;
        const currentHandleSend = handleSendRef.current;
        
        // Only send if there's content or uploading
        if (trimmedText || trimmedHtml || currentUploading) {
          if (currentSelectedUser || currentSelectedGroup) {
            // Prevent default and stop propagation
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            
            // Create a synthetic event for handleSend and pass content directly
            const syntheticEvent = { preventDefault: () => {} };
            currentHandleSend(syntheticEvent, textContent, htmlContent);
          }
        }
      }
    };

    // Function to add capture phase listener
    const addCaptureListener = () => {
      const editorElement = quillRef.current?.getEditor()?.root;
      const containerElement = editorElement?.closest('.ql-container');
      if (containerElement) {
        containerElement.addEventListener('keydown', handleKeyDownCapture, true);
        return containerElement;
      }
      return null;
    };

    // Try to setup immediately
    let containerElement = null;
    if (setupKeyboardBindings()) {
      // Successfully set up - now add capture listener
      containerElement = addCaptureListener();
    } else {
      // If not ready, try again after a short delay
      timeoutId = setTimeout(() => {
        if (setupKeyboardBindings()) {
          containerElement = addCaptureListener();
        } else {
          // If still not ready, poll until it is
          let attempts = 0;
          const maxAttempts = 50; // Try for up to 5 seconds (50 * 100ms)
          
          intervalId = setInterval(() => {
            attempts++;
            if (setupKeyboardBindings() || attempts >= maxAttempts) {
              if (setupKeyboardBindings()) {
                containerElement = addCaptureListener();
              }
              if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
              }
            }
          }, 100);
        }
      }, 100);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
      // Get container element again for cleanup
      const editorElement = quillRef.current?.getEditor()?.root;
      const cleanupContainer = editorElement?.closest('.ql-container');
      if (cleanupContainer) {
        cleanupContainer.removeEventListener('keydown', handleKeyDownCapture, true);
      }
    };
  }, [conversationId, selectedUser, selectedGroup]); // Re-run when conversation or chat type changes

  // Setup Quill image paste handler
  useEffect(() => {
    if (!quillRef.current) return;

    const quill = quillRef.current.getEditor();
    
    // Custom handler for pasting images
    const handlePaste = async (e) => {
      const clipboardData = e.clipboardData || window.clipboardData;
      if (!clipboardData) return;

      const items = clipboardData.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        // Check if the item is an image
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          e.stopImmediatePropagation();
          const file = item.getAsFile();
          
          if (!file || (!selectedUser && !selectedGroup)) return;
          
          // Check if it's an image or GIF
          if (file.type.startsWith('image/')) {
            setUploading(true);
            setUploadProgress(0);
            try {
              const response = await filesAPI.uploadFile(
                file, 
                conversationId, 
                conversationType,
                (progress) => {
                  setUploadProgress(progress);
                }
              );
              // Get current apiBaseUrl from state - if not ready, fetch it
              let currentApiBaseUrl = apiBaseUrl;
              if (!currentApiBaseUrl) {
                currentApiBaseUrl = await getApiBaseUrl();
                setApiBaseUrl(currentApiBaseUrl);
              }
              const fileUrl = `${currentApiBaseUrl.replace('/api', '')}${response.data.fileUrl}`;
              
              // Insert image into Quill editor
              const range = quill.getSelection(true);
              quill.insertEmbed(range.index, 'image', fileUrl);
              quill.setSelection(range.index + 1);
              
              // Update state
              const text = quill.getText();
              const html = quill.root.innerHTML;
              setMessageText(text);
              setMessageHtml(html);
            } catch (error) {
              console.error('Image upload error:', error);
              alert('Failed to upload image. Please try again.');
            } finally {
              setUploading(false);
              setUploadProgress(0);
            }
          }
          return;
        }
      }
    };

    // Attach paste handler in capture phase so we run before Quill's built-in
    // image paste (which would insert the image again and cause duplicates)
    const editorElement = quill.root;
    editorElement.addEventListener('paste', handlePaste, true);

    return () => {
      editorElement.removeEventListener('paste', handlePaste, true);
    };
  }, [conversationId, conversationType, selectedUser, selectedGroup]);

  if (!selectedUser && !selectedGroup) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Select a user or group to start chatting
        </Typography>
      </Box>
    );
  }

  const conversationMessages = messages.filter(
    msg => msg.conversationId === conversationId
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
      {/* Header */}
      <Paper sx={{ p: 2, borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            {conversationType === 'group' && selectedGroup ? (
              <>
                <UserAvatar name={selectedGroup.name} />
                <Box>
                  <Typography variant="h6">{selectedGroup.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Group • {selectedGroup.userIds.length} members
                  </Typography>
                </Box>
              </>
            ) : selectedUser ? (
              <>
                <UserAvatar name={selectedUser.username} online={selectedUser.online} away={selectedUser.away} profilePictureUrl={selectedUser.profilePictureUrl} />
                <Box>
                  <Typography variant="h6">{selectedUser.username}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedUser.online && !selectedUser.away ? 'Online' : selectedUser.online && selectedUser.away ? 'Away' : 'Offline'}
                  </Typography>
                </Box>
              </>
            ) : null}
          </Box>
          {conversationType === 'group' && selectedGroup && onHideGroup && (
            <IconButton
              onClick={() => onHideGroup(selectedGroup.id)}
              color="default"
              title="Hide group chat"
              sx={{ ml: 'auto' }}
            >
              <VisibilityOffIcon />
            </IconButton>
          )}
        </Box>
      </Paper>

      <Divider />

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          bgcolor: 'grey.50',
        }}
      >
        {conversationMessages.length > 0 || typingUsers.length > 0 ? (
          <List>
            {conversationMessages.map((msg) => {
              const isOwn = msg.senderId === currentUser.id;
              return (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  isOwn={isOwn}
                  currentUser={currentUser}
                  users={users}
                  conversationType={conversationType}
                  expandedMessages={expandedMessages}
                  copiedMessageId={copiedMessageId}
                onToggleExpand={toggleMessageExpand}
                onCopyMessage={handleCopyMessage}
                onEditMessage={handleEditMessage}
                onDeleteMessage={handleDeleteMessage}
                isMessageLong={isMessageLong}
                  formatFileSize={formatFileSize}
                  API_BASE_URL={apiBaseUrl}
                />
              );
            })}
            {typingUsers.length > 0 && (
              <ListItem>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {conversationType === 'group' ? 'Someone' : selectedUser?.username} is typing...
                </Typography>
              </ListItem>
            )}
            <div ref={messagesEndRef} />
          </List>
        ) : null}
      </Box>

      <Divider />

      {/* Input */}
      <Paper 
        sx={{ 
          p: 2, 
          borderRadius: 0,
          position: 'relative',
          transition: 'all 0.2s ease',
          ...(isDragging && {
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              border: '2px dashed',
              borderColor: 'primary.main',
              borderRadius: '4px',
              backgroundColor: 'primary.light',
              opacity: 0.1,
              zIndex: 1,
              pointerEvents: 'none',
            },
            '&::after': {
              content: '"Drop files here"',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'primary.main',
              fontWeight: 'bold',
              fontSize: '0.875rem',
              zIndex: 2,
              pointerEvents: 'none',
              backgroundColor: 'white',
              padding: '8px 16px',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            },
          }),
        }}
      >
        {editingMessageId && (
          <Box sx={{ mb: 1, p: 1, bgcolor: 'primary.light', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'primary.contrastText' }}>
              Editing message...
            </Typography>
            <IconButton
              size="small"
              onClick={handleCancelEdit}
              sx={{ color: 'primary.contrastText' }}
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        {uploading && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Uploading file... {uploadProgress}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}
        {pendingFileAttachment && !uploading && (
          <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <AttachFileIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {pendingFileAttachment.fileName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(pendingFileAttachment.fileSize)}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={handleRemovePendingAttachment}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <form 
          onSubmit={handleSend}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <IconButton
              onClick={handleAttachClick}
              disabled={uploading || editingMessageId}
              color="primary"
              sx={{ 
                bgcolor: 'grey.200', 
                '&:hover': { bgcolor: 'grey.300' },
                '&:disabled': { bgcolor: 'grey.100' }
              }}
            >
              {uploading ? (
                <CircularProgress size={20} />
              ) : (
                <AttachFileIcon />
              )}
            </IconButton>
            <Box 
              sx={{ 
                flex: 1,
                position: 'relative',
                transition: 'all 0.2s ease',
                '& .ql-container': {
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                },
                '& .ql-editor': {
                  minHeight: '40px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  '&.ql-blank::before': {
                    fontStyle: 'normal',
                    color: 'rgba(0, 0, 0, 0.6)',
                  },
                },
                '& .ql-toolbar': {
                  borderTop: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: '4px 4px 0 0',
                },
                '& .ql-container': {
                  borderBottom: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderTop: 'none',
                  borderRadius: '0 0 4px 4px',
                },
              }}
            >
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={messageHtml}
                onChange={handleTyping}
                placeholder="Type a message..."
                modules={quillModules}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '4px',
                }}
              />
            </Box>
            <IconButton
              type="submit"
              color="primary"
              disabled={(!messageText.trim() && !messageHtml.trim() && !pendingFileAttachment && !uploading) || uploading}
              sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}

export default ChatPanel;


